import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label, Textarea } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { STAGE_TONE } from "./InvestmentRegister";
import FundabilityPanel from "./FundabilityPanel";
import FundingGateDialog from "./FundingGateDialog";
import { downloadDossier } from "./ApplicationsTab";
import AiDraftsPanel from "./AiDraftsPanel";
import FundingRoutePanel from "./FundingRoutePanel";
import { FileDown } from "lucide-react";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function Field({ label, children }) {
  return <div><Label className="text-[10px]">{label}</Label>{children}</div>;
}

function RoiSummary({ id }) {
  const [roi, setRoi] = useState(null);
  useEffect(() => { if (id) api.get(`/initiatives/${id}/roi`).then((r) => setRoi(r.data.roi)).catch(() => {}); }, [id]);
  if (!roi) return null;
  if (roi.needs_investment) return <p className="text-xs text-muted-foreground">{roi.message}</p>;
  const rows = [
    ["Payback", roi.payback_years_before_financing ? `${roi.payback_years_before_financing} yrs` : "—"],
    ["NPV", roi.npv != null ? formatSgd(roi.npv) : "—"],
    ["ROI", roi.roi_pct_before_financing != null ? `${roi.roi_pct_before_financing}%` : "—"],
    ["Cost / tCO₂e", roi.cost_per_tco2e != null ? formatSgd(roi.cost_per_tco2e) : "—"],
  ];
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="sf-detail-roi">
      {rows.map(([l, v]) => (
        <div key={l} className="border border-border rounded-sm p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
          <div className="font-mono text-sm font-bold mt-0.5">{v}</div>
        </div>
      ))}
    </div>
  );
}

export default function RegisterDetailSheet({ id, open, onClose, meta, onChanged, onOpenCaseBuilder, onOpenFundingMatches, onOpenMrv, onNavigate, onOpenFinancingCase }) {
  const [i, setI] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [gateInfo, setGateInfo] = useState(null);

  useEffect(() => {
    if (!id || !open) return;
    setI(null);
    api.get(`/initiatives/${id}`).then((r) => { setI(r.data); setF(r.data); }).catch(() => {});
  }, [id, open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setMrv = (k, v) => setF((p) => ({ ...p, mrv: { ...(p.mrv || {}), [k]: v } }));
  const setImpact = (k, v) => setF((p) => ({ ...p, impact: { ...(p.impact || {}), [k]: num(v) } }));

  const save = async () => {
    setBusy(true);
    try {
      const envT = (meta?.linkable_env_targets || []).find((t) => t.id === f.linked_target_id);
      const body = {
        initiative_name: f.initiative_name, initiative_description: f.initiative_description,
        owner: f.owner, priority: f.priority, status: f.status, climate_response: f.climate_response,
        esg_problem: f.esg_problem, baseline_value: f.baseline_value, baseline_year: num(f.baseline_year),
        material_issue: undefined, target_kpi: f.target_kpi || undefined,
        linked_target_id: f.linked_target_id || undefined,
        linked_target_label: envT ? `${envT.name} (FY${envT.target_year})` : f.linked_target_label || undefined,
        linked_risk: f.linked_risk || undefined,
        physical_or_transition: f.physical_or_transition || undefined,
        risk_exposure_sgd: num(f.risk_exposure_sgd), risk_reduction_sgd: num(f.risk_reduction_sgd),
        technology: f.technology, vendor: f.vendor, implementation_approach: f.implementation_approach,
        useful_life_years: num(f.useful_life_years), start_date: f.start_date || undefined,
        target_completion_date: f.target_completion_date || undefined,
        investment_required: num(f.investment_required), expected_annual_savings: num(f.expected_annual_savings),
        expected_carbon_reduction: num(f.expected_carbon_reduction), expected_carbon_tax_avoided: num(f.expected_carbon_tax_avoided),
        approved_budget: num(f.approved_budget), actual_spend: num(f.actual_spend),
        funding_required: num(f.funding_required),
        potential_grant_sgd: num(f.potential_grant_sgd), potential_financing_sgd: num(f.potential_financing_sgd),
        company_contribution_sgd: num(f.company_contribution_sgd),
        impact: f.impact, mrv: f.mrv,
      };
      Object.keys(body).forEach((k) => (body[k] === undefined || body[k] === null) && delete body[k]);
      const r = await api.patch(`/initiatives/${id}`, body);
      setI(r.data); setF(r.data); onChanged?.();
      toast.success("Investment saved");
    } catch (e) {
      const gate = e?.response?.data?.detail?.gate;
      if (gate) setGateInfo(gate);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const stageLabel = i ? (meta?.stage_labels || {})[i.pipeline_stage] || i.pipeline_stage : "";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="sf-detail-sheet">
        {!i ? <div className="p-6 text-muted-foreground font-mono text-sm">Loading…</div> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display flex items-center gap-2 flex-wrap pr-8">
                {i.initiative_name}
                <Badge variant="outline" className={`text-[9px] uppercase ${STAGE_TONE[i.pipeline_stage] || ""}`} data-testid="sf-detail-stage">{stageLabel}</Badge>
              </SheetTitle>
            </SheetHeader>
            <p className="text-xs text-muted-foreground -mt-1 mb-3 flex items-center justify-between gap-2">
              <span>{(i.initiative_type || "").replace(/_/g, " ")} · {i.owner} · {i.progress_percent}% complete</span>
              <button onClick={() => downloadDossier(id, i.initiative_name)}
                className="text-accent hover:underline inline-flex items-center gap-1 shrink-0"
                data-testid="sf-detail-dossier-btn">
                <FileDown className="h-3.5 w-3.5" /> Dossier PDF
              </button>
            </p>

            <Tabs defaultValue="overview">
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="overview" data-testid="sf-detail-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="esg" data-testid="sf-detail-tab-esg">ESG & Climate</TabsTrigger>
                <TabsTrigger value="technical" data-testid="sf-detail-tab-technical">Technical</TabsTrigger>
                <TabsTrigger value="financial" data-testid="sf-detail-tab-financial">Financial</TabsTrigger>
                <TabsTrigger value="funding" data-testid="sf-detail-tab-funding">Funding</TabsTrigger>
                <TabsTrigger value="mrv" data-testid="sf-detail-tab-mrv">MRV</TabsTrigger>
                <TabsTrigger value="readiness" data-testid="sf-detail-tab-readiness">Readiness</TabsTrigger>
                <TabsTrigger value="drafts" data-testid="sf-detail-tab-drafts">AI Drafts</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-3">
                <Field label="Name"><Input value={f.initiative_name || ""} onChange={(e) => set("initiative_name", e.target.value)} data-testid="sf-detail-name" /></Field>
                <Field label="Description"><Textarea rows={3} value={f.initiative_description || ""} onChange={(e) => set("initiative_description", e.target.value)} /></Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Owner"><Input value={f.owner || ""} onChange={(e) => set("owner", e.target.value)} /></Field>
                  <Field label="Priority">
                    <Select value={f.priority || "medium"} onValueChange={(v) => set("priority", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select></Field>
                  <Field label="Status">
                    <Select value={f.status || "draft"} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="sf-detail-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select></Field>
                </div>
              </TabsContent>

              <TabsContent value="esg" className="mt-4 space-y-3">
                <Field label="ESG problem / opportunity"><Textarea rows={2} value={f.esg_problem || ""} onChange={(e) => set("esg_problem", e.target.value)} data-testid="sf-detail-problem" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Baseline (value & unit)"><Input value={f.baseline_value || ""} onChange={(e) => set("baseline_value", e.target.value)} placeholder="2.1 GWh electricity" /></Field>
                  <Field label="Baseline year"><Input type="number" value={f.baseline_year ?? ""} onChange={(e) => set("baseline_year", e.target.value)} /></Field>
                  <Field label="Contributes to target">
                    <Select value={f.linked_target_id || "none"} onValueChange={(v) => set("linked_target_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="sf-detail-target"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {(meta?.linkable_env_targets || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (FY{t.target_year})</SelectItem>)}
                      </SelectContent>
                    </Select></Field>
                  <Field label="Scorecard KPI">
                    <Select value={f.target_kpi || "none"} onValueChange={(v) => set("target_kpi", v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {(meta?.linkable_targets || []).map((t) => <SelectItem key={t.id} value={t.kpi_name}>{t.kpi_name}</SelectItem>)}
                      </SelectContent>
                    </Select></Field>
                  <Field label="Climate risk addressed">
                    <Select value={f.linked_risk || "none"} onValueChange={(v) => set("linked_risk", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="sf-detail-risk"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {(meta?.linkable_risks || []).map((t) => <SelectItem key={t.id} value={t.title}>{t.title}</SelectItem>)}
                      </SelectContent>
                    </Select></Field>
                  <Field label="Risk classification">
                    <Select value={f.physical_or_transition || "none"} onValueChange={(v) => set("physical_or_transition", v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="physical">Physical risk</SelectItem>
                        <SelectItem value="transition">Transition risk</SelectItem>
                      </SelectContent>
                    </Select></Field>
                  <Field label="Risk exposure (S$)"><Input type="number" value={f.risk_exposure_sgd ?? ""} onChange={(e) => set("risk_exposure_sgd", e.target.value)} data-testid="sf-detail-exposure" /></Field>
                  <Field label="Risk reduction (S$/yr)"><Input type="number" value={f.risk_reduction_sgd ?? ""} onChange={(e) => set("risk_reduction_sgd", e.target.value)} /></Field>
                </div>
                <div className="label-eyebrow text-[10px] pt-1">Environmental impact (per year)</div>
                <div className="grid grid-cols-4 gap-3">
                  <Field label="tCO₂e"><Input type="number" value={f.impact?.emissions_t ?? ""} onChange={(e) => setImpact("emissions_t", e.target.value)} /></Field>
                  <Field label="MWh"><Input type="number" value={f.impact?.energy_mwh ?? ""} onChange={(e) => setImpact("energy_mwh", e.target.value)} /></Field>
                  <Field label="Water m³"><Input type="number" value={f.impact?.water_m3 ?? ""} onChange={(e) => setImpact("water_m3", e.target.value)} /></Field>
                  <Field label="Waste t"><Input type="number" value={f.impact?.waste_t ?? ""} onChange={(e) => setImpact("waste_t", e.target.value)} /></Field>
                </div>
              </TabsContent>

              <TabsContent value="technical" className="mt-4 grid grid-cols-2 gap-3">
                <Field label="Technology"><Input value={f.technology || ""} onChange={(e) => set("technology", e.target.value)} data-testid="sf-detail-technology" /></Field>
                <Field label="Vendor"><Input value={f.vendor || ""} onChange={(e) => set("vendor", e.target.value)} /></Field>
                <div className="col-span-2"><Field label="Implementation approach"><Textarea rows={2} value={f.implementation_approach || ""} onChange={(e) => set("implementation_approach", e.target.value)} /></Field></div>
                <Field label="Asset life (years)"><Input type="number" value={f.useful_life_years ?? ""} onChange={(e) => set("useful_life_years", e.target.value)} /></Field>
                <div />
                <Field label="Start date"><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
                <Field label="Target completion"><Input type="date" value={f.target_completion_date || ""} onChange={(e) => set("target_completion_date", e.target.value)} /></Field>
              </TabsContent>

              <TabsContent value="financial" className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CAPEX (S$)"><Input type="number" value={f.investment_required ?? ""} onChange={(e) => set("investment_required", e.target.value)} data-testid="sf-detail-capex" /></Field>
                  <Field label="Annual savings (S$)"><Input type="number" value={f.expected_annual_savings ?? ""} onChange={(e) => set("expected_annual_savings", e.target.value)} data-testid="sf-detail-savings" /></Field>
                  <Field label="Carbon reduction (t/yr)"><Input type="number" value={f.expected_carbon_reduction ?? ""} onChange={(e) => set("expected_carbon_reduction", e.target.value)} /></Field>
                  <Field label="Carbon tax avoided (S$/yr)"><Input type="number" value={f.expected_carbon_tax_avoided ?? ""} onChange={(e) => set("expected_carbon_tax_avoided", e.target.value)} /></Field>
                  <Field label="Approved budget (S$)"><Input type="number" value={f.approved_budget ?? ""} onChange={(e) => set("approved_budget", e.target.value)} /></Field>
                  <Field label="Actual spend (S$)"><Input type="number" value={f.actual_spend ?? ""} onChange={(e) => set("actual_spend", e.target.value)} /></Field>
                </div>
                <RoiSummary id={id} />
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-muted-foreground">
                    Model grant & debt scenarios, avoided losses and full cash flows in the Investment Case Builder.
                  </p>
                  {onOpenCaseBuilder && (
                    <Button size="sm" variant="outline" onClick={() => onOpenCaseBuilder(id)} data-testid="sf-detail-open-case-builder">
                      Open Case Builder
                    </Button>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="funding" className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Potential grant (S$)"><Input type="number" value={f.potential_grant_sgd ?? ""} onChange={(e) => set("potential_grant_sgd", e.target.value)} data-testid="sf-detail-grant" /></Field>
                  <Field label="Potential green financing (S$)"><Input type="number" value={f.potential_financing_sgd ?? ""} onChange={(e) => set("potential_financing_sgd", e.target.value)} data-testid="sf-detail-financing" /></Field>
                  <Field label="Company contribution (S$)"><Input type="number" value={f.company_contribution_sgd ?? ""} onChange={(e) => set("company_contribution_sgd", e.target.value)} /></Field>
                  <Field label="External funding required (S$)"><Input type="number" value={f.funding_required ?? ""} onChange={(e) => set("funding_required", e.target.value)} /></Field>
                </div>
                <div className="border border-border rounded-sm p-3 text-xs space-y-1.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Funding gap</span><span className="font-mono">{i.funding_gap != null ? formatSgd(i.funding_gap) : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Financing match status</span><span className="capitalize">{(i.financing_match_status || "not assessed").replace(/_/g, " ")}</span></div>
                  {i.tagged_finance_offer && <div className="flex justify-between"><span className="text-muted-foreground">Tagged offer</span><span>{i.tagged_finance_offer.bank_name} — {i.tagged_finance_offer.offer_name}</span></div>}
                  {i.green_financing_case_id && <div className="flex justify-between"><span className="text-muted-foreground">Financing case</span><span className="font-mono">{i.green_financing_case_id.slice(-6)}</span></div>}
                </div>
                <FundingRoutePanel iid={id} onNavigate={onNavigate}
                  onOpenFinancingCase={onOpenFinancingCase ? (cid) => { onClose(); onOpenFinancingCase(cid); } : undefined}
                  onChanged={onChanged} />
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-muted-foreground">
                    Screen this investment against Singapore schemes and bank offers with confidence tiers and opportunity scores.
                  </p>
                  {onOpenFundingMatches && (
                    <Button size="sm" variant="outline" onClick={() => onOpenFundingMatches(id)} data-testid="sf-detail-open-funding-matches">
                      Run funding discovery
                    </Button>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="mrv" className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Measurement method"><Input value={f.mrv?.measurement_method || ""} onChange={(e) => setMrv("measurement_method", e.target.value)} data-testid="sf-detail-mrv-method" placeholder="Sub-metered kWh vs baseline" /></Field>
                  <Field label="Data source"><Input value={f.mrv?.data_source || ""} onChange={(e) => setMrv("data_source", e.target.value)} placeholder="Utility bills / BMS export" /></Field>
                  <Field label="Frequency">
                    <Select value={f.mrv?.frequency || "none"} onValueChange={(v) => setMrv("frequency", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="sf-detail-mrv-frequency"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select></Field>
                  <Field label="Verifier"><Input value={f.mrv?.verifier || ""} onChange={(e) => setMrv("verifier", e.target.value)} placeholder="Internal / external assurer" /></Field>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-muted-foreground flex-1">
                    Define how the promised impact will be measured before funding is sought. Record actual
                    readings and track expected-vs-actual variance in the MRV workspace.
                  </p>
                  {onOpenMrv && (
                    <Button size="sm" variant="outline" onClick={() => onOpenMrv(id)} data-testid="sf-detail-open-mrv">
                      Open MRV workspace
                    </Button>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="readiness" className="mt-4">
                <FundabilityPanel id={id} />
              </TabsContent>

              <TabsContent value="drafts" className="mt-4">
                <AiDraftsPanel iid={id} />
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 mt-5 pb-2">
              <Button variant="outline" onClick={onClose} data-testid="sf-detail-close">Close</Button>
              <Button variant="primary" onClick={save} disabled={busy} data-testid="sf-detail-save">{busy ? "Saving…" : "Save changes"}</Button>
            </div>

            <FundingGateDialog iid={id} gate={gateInfo} open={!!gateInfo} onClose={() => setGateInfo(null)}
              onReviewFunding={() => { setGateInfo(null); onOpenFundingMatches?.(id); }}
              onResolved={() => { setGateInfo(null); save(); }} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
